// File: src/pages/buyer/AddProduct/sections/BasicDetails.jsx
import React, { useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { loadGoogleMaps } from "../../../../utils/loadGoogleMaps";

const BasicDetails = ({ categories = [], subCategories = [], setValue }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize Google Places Autocomplete (Worldwide)
  useEffect(() => {
    if (isEditMode) return;
    if (!inputRef.current) return;

    loadGoogleMaps().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(
        inputRef.current,
        { types: ["geocode"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place?.formatted_address && place.geometry) {
          setValue("location", place.formatted_address, {
            shouldValidate: true,
          });
          setValue("latitude", place.geometry.location.lat());
          setValue("longitude", place.geometry.location.lng());
        }
      });

      autocompleteRef.current = autocomplete;
    });
  }, [setValue, isEditMode]);

  // Get Current Location (Geolocation API)
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const address = results[0].formatted_address;
              setValue("location", address);
            } else {
              alert(
                "Unable to retrieve your address. Please type it manually."
              );
            }
          }
        );
      },
      (error) => {
        console.error("Error getting location:", error);
        alert(
          "Unable to access your location. Please enable location permissions."
        );
      }
    );
  };

  return (
    <div className="border rounded p-4 mb-4 position-relative bg-white">
      <h5 className="fw-semibold mb-4">Basic Details</h5>

      <Row className="g-3">
        {/* Category */}
        <Col md={4}>
          <Form.Group>
            <Form.Label>Category</Form.Label>
            <Controller
              name="category_id"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("subcategory_id", "");
                    setValue("metal_code", "");
                  }}
                  disabled={isEditMode}
                >
                  <option value="">Select</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            {errors.category_id && (
              <div className="text-danger small">
                {errors.category_id.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Subcategory */}
        <Col md={4}>
          <Form.Group>
            <Form.Label>Subcategory</Form.Label>
            <Controller
              name="subcategory_id"
              control={control}
              render={({ field }) => (
                <Form.Select {...field} disabled={isEditMode}>
                  <option value="">Select</option>
                  {subCategories.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            {errors.subcategory_id && (
              <div className="text-danger small">
                {errors.subcategory_id.message}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Metal Code */}
        <Col md={4}>
          <Form.Group>
            <Form.Label>Metal Code</Form.Label>
            <Controller
              name="metal_code"
              control={control}
              render={({ field }) => <Form.Control {...field} disabled />}
            />
          </Form.Group>
        </Col>

        {/* Location */}
        <Col md={8}>
          <Form.Group>
            <Form.Label>Location</Form.Label>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  ref={(el) => {
                    field.ref(el);
                    inputRef.current = el;
                  }}
                  placeholder="Start typing to search or use current location"
                  disabled={isEditMode}
                />
              )}
            />
            {errors.location && (
              <div className="text-danger small">{errors.location.message}</div>
            )}
          </Form.Group>
        </Col>

        {/* Button */}
        <Col md={4} className="d-flex align-items-end">
          <Button
            variant="primary"
            className="w-80"
            onClick={handleUseCurrentLocation}
          >
            Use Current Location
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default React.memo(BasicDetails);
